import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Monitor, Tablet, Smartphone, Save, Rocket, Loader2, Palette, Search,
  Sparkles, Check, ArrowLeft, Copy, ExternalLink, X, Gauge, Image as ImageIcon, Trash2, Upload,
  Globe, CheckCircle2, RefreshCw
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Logo } from "@/components/Navbar";
import WebsiteRenderer from "@/components/WebsiteRenderer";
import { useAuth } from "@/context/AuthContext";

const DEVICES = { desktop: 1180, tablet: 800, mobile: 390 };

export default function Editor() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isFree = (user?.plan_limit ?? 1) <= 1;
  const [project, setProject] = useState(null);
  const [website, setWebsite] = useState(null);
  const [vp, setVp] = useState("desktop");
  const [tab, setTab] = useState("design");
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(null);
  const [showPublish, setShowPublish] = useState(false);
  const [subdomain, setSubdomain] = useState("");
  const saveTimer = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        await api.post(`/projects/${id}/claim`);
        const { data } = await api.get(`/projects/${id}`);
        if (!data.website) { toast.error("This project has no website yet."); nav("/dashboard"); return; }
        setProject(data);
        setWebsite(data.website);
        setPublished(data.published ? data.subdomain : null);
        setSubdomain(data.subdomain || slug(data.profile?.business?.name));
        setCustomDomain(data.custom_domain || "");
        setDomainInput(data.custom_domain || "");
        setDomainStatus(data.domain_status || "none");
      } catch (e) {
        toast.error(apiError(e));
        nav("/dashboard");
      }
    })();
  }, [id, nav]);

  const persist = useCallback(async (w) => {
    setSaving(true);
    try { await api.put(`/projects/${id}`, { website: w }); } catch (e) { /* silent */ }
    setSaving(false);
  }, [id]);

  const update = (w) => {
    setWebsite(w);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persist(w), 1200);
  };

  const setColor = (k, v) => update({ ...website, colors: { ...website.colors, [k]: v } });
  const setFont = (k, v) => update({ ...website, fonts: { ...website.fonts, [k]: v } });

  const [uploading, setUploading] = useState(false);
  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      update({ ...website, media: [...(website.media || []), data.url] });
      toast.success("Photo added.");
    } catch (err) { toast.error(apiError(err)); }
    setUploading(false);
  };
  const removePhoto = (i) => update({ ...website, media: (website.media || []).filter((_, j) => j !== i) });

  // ---- Domain management ----
  const [showDomain, setShowDomain] = useState(false);
  const [domainInput, setDomainInput] = useState("");
  const [dnsRecords, setDnsRecords] = useState(null);
  const [customDomain, setCustomDomain] = useState("");
  const [domainStatus, setDomainStatus] = useState("none");
  const [domainBusy, setDomainBusy] = useState(false);

  const saveSubdomain = async () => {
    setDomainBusy(true);
    try {
      const { data } = await api.put(`/projects/${id}/subdomain`, { subdomain });
      setSubdomain(data.subdomain);
      if (published) setPublished(data.subdomain);
      toast.success("Address updated.");
    } catch (e) { toast.error(apiError(e)); }
    setDomainBusy(false);
  };

  const connectDomain = async () => {
    setDomainBusy(true);
    try {
      const { data } = await api.post(`/projects/${id}/domain`, { domain: domainInput });
      setCustomDomain(data.domain);
      setDnsRecords(data.dns_records);
      setDomainStatus(data.status);
      toast.success("Domain added — now add the DNS records below.");
    } catch (e) { toast.error(apiError(e)); }
    setDomainBusy(false);
  };

  const verifyDomain = async () => {
    setDomainBusy(true);
    try {
      const { data } = await api.post(`/projects/${id}/domain/verify`);
      setDomainStatus(data.status);
      if (data.status === "verified") toast.success("Domain verified! 🎉 It goes live when you deploy.");
      else toast.message(data.detail || "Not verified yet — DNS can take a few minutes.");
    } catch (e) { toast.error(apiError(e)); }
    setDomainBusy(false);
  };

  const removeDomain = async () => {
    setDomainBusy(true);
    try {
      await api.delete(`/projects/${id}/domain`);
      setCustomDomain(""); setDnsRecords(null); setDomainStatus("none"); setDomainInput("");
      toast.success("Domain removed.");
    } catch (e) { toast.error(apiError(e)); }
    setDomainBusy(false);
  };

  const doPublish = async () => {
    setPublishing(true);
    try {
      await persist(website);
      const { data } = await api.post(`/projects/${id}/publish`, { subdomain: slug(subdomain) });
      setPublished(data.subdomain);
      setShowPublish(true);
    } catch (e) { toast.error(apiError(e)); }
    setPublishing(false);
  };

  const suggestions = buildSuggestions(project, website, update);

  if (!website) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  }

  const siteUrl = published ? `${window.location.origin}/site/${published}` : null;

  return (
    <div className="h-screen flex flex-col bg-[#f4f4f5] overflow-hidden">
      {/* Top bar */}
      <div className="bg-white border-b border-border px-4 h-14 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-4">
          <Link to="/dashboard" className="text-[#52525b] hover:text-[#09090b]" data-testid="editor-back"><ArrowLeft size={18} /></Link>
          <Logo />
          <span className="hidden sm:inline font-body text-sm text-[#52525b]">/ {project?.profile?.business?.name}</span>
        </div>
        <div className="flex items-center gap-1 bg-[#f4f4f5] rounded-full p-1">
          {Object.keys(DEVICES).map((d) => {
            const Icon = d === "desktop" ? Monitor : d === "tablet" ? Tablet : Smartphone;
            return (
              <button key={d} data-testid={`device-${d}`} onClick={() => setVp(d)}
                className={`w-8 h-8 rounded-full flex items-center justify-center ${vp === d ? "bg-white shadow-sm text-[#09090b]" : "text-[#a1a1aa]"}`}>
                <Icon size={16} />
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          <span className="hidden sm:flex items-center gap-1.5 text-xs font-body text-[#a1a1aa]">
            {saving ? <><Loader2 size={12} className="animate-spin" /> Saving…</> : <><Check size={12} className="text-emerald-500" /> Saved</>}
          </span>
          <button data-testid="editor-save" onClick={() => persist(website)} className="border border-border rounded-full px-4 py-2 text-sm font-semibold font-body inline-flex items-center gap-1.5">
            <Save size={14} /> Save
          </button>
          <button data-testid="editor-domain" onClick={() => setShowDomain(true)} className="border border-border rounded-full px-4 py-2 text-sm font-semibold font-body inline-flex items-center gap-1.5">
            <Globe size={14} /> Domain
          </button>
          <button data-testid="editor-publish" onClick={() => setShowPublish(true)} className="ml-ai-btn rounded-full px-4 py-2 text-sm font-semibold font-body inline-flex items-center gap-1.5">
            <Rocket size={14} /> {published ? "Published" : "Publish"}
          </button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Canvas */}
        <div className="flex-1 overflow-auto ml-scrollbar p-6 flex justify-center">
          <div className="transition-all duration-300 mx-auto" style={{ width: DEVICES[vp], maxWidth: "100%" }}>
            <div className="bg-white rounded-xl overflow-hidden border border-border shadow-[0_10px_30px_-8px_rgba(0,0,0,0.12)]">
              <WebsiteRenderer website={website} profile={project.profile} editable onChange={update} vp={vp} />
            </div>
            <p className="text-center text-xs text-[#a1a1aa] font-body mt-3">Tip: click any text on the page to edit it directly.</p>
          </div>
        </div>

        {/* Sidebar */}
        <aside className="w-[340px] bg-white border-l border-border flex flex-col shrink-0">
          {isFree && (
            <a href="/pricing" data-testid="editor-upgrade-banner"
              className="flex items-center gap-2 px-4 py-2.5 ml-ai-btn text-white text-xs font-body font-semibold">
              <Sparkles size={14} /> Upgrade to Pro — custom domain, 5 sites & remove badge
            </a>
          )}
          <div className="flex border-b border-border">
            {[["design", Palette, "Design"], ["photos", ImageIcon, "Photos"], ["seo", Search, "SEO"], ["ai", Sparkles, "AI Ideas"]].map(([k, Icon, label]) => (
              <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
                className={`flex-1 py-3 text-sm font-body font-medium flex items-center justify-center gap-1.5 ${tab === k ? "text-[#09090b] border-b-2 border-[#09090b]" : "text-[#a1a1aa]"}`}>
                <Icon size={14} /> {label}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-auto ml-scrollbar p-5">
            {tab === "design" && (
              <div className="space-y-6">
                <div>
                  <div className="ml-label mb-3">Brand Colors</div>
                  {["primary", "secondary", "accent", "bg", "text"].map((k) => (
                    <div key={k} className="flex items-center justify-between mb-2.5">
                      <span className="font-body text-sm text-[#52525b] capitalize">{k === "bg" ? "Background" : k}</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono-ml text-xs text-[#a1a1aa]">{website.colors?.[k]}</span>
                        <input data-testid={`color-${k}`} type="color" value={website.colors?.[k] || "#000000"} onChange={(e) => setColor(k, e.target.value)}
                          className="w-8 h-8 rounded-md border border-border cursor-pointer" />
                      </div>
                    </div>
                  ))}
                </div>
                <div>
                  <div className="ml-label mb-3">Typography</div>
                  {["heading", "body"].map((k) => (
                    <div key={k} className="mb-3">
                      <label className="font-body text-sm text-[#52525b] capitalize">{k} font</label>
                      <select data-testid={`font-${k}`} value={website.fonts?.[k] || ""} onChange={(e) => setFont(k, e.target.value)}
                        className="w-full mt-1 border border-border rounded-lg px-3 py-2 font-body text-sm outline-none">
                        {["Outfit", "Manrope", "Poppins", "Playfair Display", "Montserrat", "Lora", "Inter", "DM Sans", "Space Grotesk", "Merriweather", website.fonts?.[k]]
                          .filter((v, i, a) => v && a.indexOf(v) === i)
                          .map((f) => <option key={f} value={f}>{f}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
                <div>
                  <div className="ml-label mb-3">Branding</div>
                  <div className="flex items-center justify-between rounded-xl border border-border p-3">
                    <div>
                      <div className="font-body text-sm text-[#09090b] font-medium flex items-center gap-1.5">
                        Remove "MapLift" badge
                        {isFree && <span className="text-[9px] font-mono-ml uppercase bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded">Pro</span>}
                      </div>
                      <div className="font-body text-xs text-[#a1a1aa] mt-0.5">Hide the footer credit on your site.</div>
                    </div>
                    {isFree ? (
                      <a href="/pricing" data-testid="badge-upgrade" className="text-xs font-semibold ml-ai-text font-body shrink-0">Upgrade</a>
                    ) : (
                      <button data-testid="toggle-badge" onClick={() => update({ ...website, remove_badge: !website.remove_badge })}
                        className={`w-11 h-6 rounded-full transition-colors shrink-0 relative ${website.remove_badge ? "bg-emerald-500" : "bg-[#e4e4e7]"}`}>
                        <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all ${website.remove_badge ? "left-[22px]" : "left-0.5"}`} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {tab === "photos" && (
              <div className="space-y-4">
                <p className="font-body text-sm text-[#52525b]">Upload your real business photos. They replace the stock images on your site (hero, about & gallery).</p>
                <label data-testid="photo-upload" className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-border rounded-xl py-8 cursor-pointer hover:bg-[#fafafa]">
                  {uploading ? <Loader2 size={22} className="animate-spin text-sky-500" /> : <Upload size={22} className="text-[#a1a1aa]" />}
                  <span className="font-body text-sm text-[#52525b]">{uploading ? "Uploading…" : "Click to upload"}</span>
                  <span className="font-mono-ml text-[10px] text-[#a1a1aa]">JPG · PNG · WEBP · max 8MB</span>
                  <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} disabled={uploading} />
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {(website.media || []).map((u, i) => (
                    <div key={i} className="relative rounded-lg overflow-hidden border border-border group">
                      <img src={u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u} alt="" className="w-full h-24 object-cover" />
                      <button data-testid={`remove-photo-${i}`} onClick={() => removePhoto(i)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <Trash2 size={13} />
                      </button>
                      {i === 0 && <span className="absolute bottom-1 left-1 text-[9px] font-mono-ml uppercase bg-sky-500 text-white px-1.5 py-0.5 rounded">Hero</span>}
                    </div>
                  ))}
                </div>
                {(!website.media || website.media.length === 0) && (
                  <p className="font-body text-xs text-[#a1a1aa] text-center">No uploads yet — your site is using imported / curated photos.</p>
                )}
              </div>
            )}

            {tab === "seo" && (
              <div className="space-y-5">
                <ScorePill label="Conversion" value={website.meta?.conversion_score} />
                <ScorePill label="SEO Health" value={website.meta?.seo_score} />
                <ScorePill label="Design Quality" value={website.meta?.design_score} />
                <div>
                  <div className="ml-label mb-2">Page Title</div>
                  <p className="font-body text-sm text-[#09090b]">{website.seo?.title}</p>
                </div>
                <div>
                  <div className="ml-label mb-2">Meta Description</div>
                  <p className="font-body text-sm text-[#52525b]">{website.seo?.description}</p>
                </div>
                <div>
                  <div className="ml-label mb-2">Target Keywords</div>
                  <div className="flex flex-wrap gap-1.5">
                    {(website.seo?.keywords || []).map((k, i) => <span key={i} className="bg-[#f4f4f5] text-xs px-2.5 py-1 rounded-full font-body">{k}</span>)}
                  </div>
                </div>
              </div>
            )}

            {tab === "ai" && (
              <div className="space-y-3">
                <p className="font-body text-sm text-[#52525b] mb-2">One-click improvements from your review analysis.</p>
                {suggestions.map((s, i) => (
                  <div key={i} className="border border-border rounded-xl p-4">
                    <div className="flex items-start gap-2">
                      <Sparkles size={15} className="text-sky-500 mt-0.5 shrink-0" />
                      <div className="flex-1">
                        <div className="font-body text-sm text-[#09090b] font-medium">{s.title}</div>
                        <div className="font-body text-xs text-[#52525b] mt-1">{s.desc}</div>
                        <button data-testid={`apply-suggestion-${i}`} onClick={s.apply}
                          className="mt-3 text-xs font-semibold ml-ai-text font-body">Apply →</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* Publish modal */}
      {showPublish && !published && (
        <Modal onClose={() => setShowPublish(false)}>
          <div className="text-center">
            <div className="w-12 h-12 rounded-xl ml-ai-btn flex items-center justify-center mx-auto mb-4"><Rocket size={22} color="#fff" /></div>
            <h3 className="font-heading font-bold text-xl text-[#09090b]">Publish your website</h3>
            <p className="font-body text-sm text-[#52525b] mt-2">Choose your free MapLift address.</p>
            <div className="flex items-center border border-border rounded-xl mt-5 overflow-hidden">
              <span className="pl-3 pr-1 font-mono-ml text-xs text-[#a1a1aa]">/site/</span>
              <input data-testid="publish-subdomain" value={subdomain} onChange={(e) => setSubdomain(e.target.value)}
                className="flex-1 py-3 font-body text-sm outline-none" />
            </div>
            <button data-testid="publish-confirm" onClick={doPublish} disabled={publishing}
              className="w-full mt-5 ml-ai-btn rounded-full py-3 font-semibold text-sm font-body inline-flex items-center justify-center gap-2">
              {publishing ? <Loader2 size={16} className="animate-spin" /> : <Rocket size={16} />} Publish Now
            </button>
          </div>
        </Modal>
      )}

      {/* Celebration modal (shown after publishing or when managing a live site) */}
      {showPublish && published && (
        <Modal onClose={() => setShowPublish(false)}>
          <PublishSuccess siteUrl={siteUrl} />
        </Modal>
      )}

      {/* Domain manager */}
      {showDomain && (
        <Modal onClose={() => setShowDomain(false)}>
          <DomainManager
            subdomain={subdomain} setSubdomain={setSubdomain} saveSubdomain={saveSubdomain}
            origin={window.location.origin} isFree={isFree}
            domainInput={domainInput} setDomainInput={setDomainInput}
            customDomain={customDomain} domainStatus={domainStatus} dnsRecords={dnsRecords}
            connectDomain={connectDomain} verifyDomain={verifyDomain} removeDomain={removeDomain}
            busy={domainBusy}
          />
        </Modal>
      )}
    </div>
  );
}

function DomainManager({ subdomain, setSubdomain, saveSubdomain, origin, domainInput, setDomainInput,
  customDomain, domainStatus, dnsRecords, connectDomain, verifyDomain, removeDomain, busy, isFree }) {
  const [tab, setTab] = useState("connect");
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };
  return (
    <div className="text-left">
      <h3 className="font-heading font-bold text-xl text-[#09090b] flex items-center gap-2"><Globe size={20} /> Domains</h3>
      <div className="flex gap-2 mt-4 mb-5">
        {[["connect", "Connect domain"], ["maplift", "MapLift address"], ["buy", "Buy new"]].map(([k, l]) => (
          <button key={k} data-testid={`domain-tab-${k}`} onClick={() => setTab(k)}
            className={`text-xs font-body font-semibold px-3 py-1.5 rounded-full ${tab === k ? "bg-[#09090b] text-white" : "bg-[#f4f4f5] text-[#52525b]"}`}>{l}</button>
        ))}
      </div>

      {tab === "maplift" && (
        <div>
          <label className="ml-label">Your free MapLift address</label>
          <div className="flex items-center border border-border rounded-xl mt-2 overflow-hidden">
            <span className="pl-3 pr-1 font-mono-ml text-xs text-[#a1a1aa] shrink-0">{origin}/site/</span>
            <input data-testid="domain-subdomain-input" value={subdomain} onChange={(e) => setSubdomain(e.target.value)} className="flex-1 py-2.5 font-body text-sm outline-none min-w-0" />
          </div>
          <button data-testid="domain-subdomain-save" onClick={saveSubdomain} disabled={busy}
            className="mt-4 ml-ai-btn rounded-full px-5 py-2.5 font-semibold text-sm font-body inline-flex items-center gap-2">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Save address
          </button>
        </div>
      )}

      {tab === "connect" && (
        <div>
          {isFree ? (
            <div className="rounded-xl border border-sky-200 bg-sky-50 p-5 text-center">
              <Sparkles size={22} className="text-sky-500 mx-auto" />
              <div className="font-heading font-semibold text-[#09090b] mt-2">Custom domains are a Pro feature</div>
              <p className="font-body text-sm text-[#52525b] mt-1">Connect your own domain like yourbusiness.com on the Pro plan.</p>
              <a href="/pricing" data-testid="domain-upgrade" className="inline-block mt-4 ml-ai-btn rounded-full px-5 py-2.5 font-semibold text-sm font-body">Upgrade to Pro</a>
            </div>
          ) : domainStatus === "verified" ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-2">
              <CheckCircle2 size={18} className="text-emerald-500 mt-0.5" />
              <div>
                <div className="font-body font-semibold text-sm text-[#09090b]">{customDomain} is verified</div>
                <div className="font-body text-xs text-[#52525b] mt-1">Your domain will serve this site once you deploy the app.</div>
              </div>
            </div>
          ) : (
            <>
              <label className="ml-label">Your domain</label>
              <div className="flex gap-2 mt-2">
                <input data-testid="domain-input" value={domainInput} onChange={(e) => setDomainInput(e.target.value)} placeholder="yourbusiness.com"
                  className="flex-1 border border-border rounded-xl px-4 py-2.5 font-body text-sm outline-none focus:ring-2 focus:ring-sky-400" />
                <button data-testid="domain-connect-btn" onClick={connectDomain} disabled={busy || !domainInput}
                  className="bg-[#09090b] text-white rounded-xl px-4 font-semibold text-sm font-body inline-flex items-center gap-1.5">
                  {busy ? <Loader2 size={15} className="animate-spin" /> : "Connect"}
                </button>
              </div>
            </>
          )}

          {dnsRecords && domainStatus !== "verified" && (
            <div className="mt-5">
              <div className="flex items-center justify-between">
                <div className="ml-label">Add these DNS records at your registrar</div>
                <span className="text-[10px] font-mono-ml uppercase px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{domainStatus}</span>
              </div>
              <div className="mt-3 space-y-2">
                {dnsRecords.map((r, i) => (
                  <div key={i} className="border border-border rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <span className="font-mono-ml text-[10px] uppercase text-[#a1a1aa]">{r.type} · {r.purpose}</span>
                      <button onClick={() => copy(r.value)} className="text-[#52525b]"><Copy size={13} /></button>
                    </div>
                    <div className="font-mono-ml text-[11px] text-[#09090b] mt-1 break-all"><span className="text-[#a1a1aa]">host</span> {r.host}</div>
                    <div className="font-mono-ml text-[11px] text-[#09090b] break-all"><span className="text-[#a1a1aa]">value</span> {r.value}</div>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-4">
                <button data-testid="domain-verify-btn" onClick={verifyDomain} disabled={busy}
                  className="ml-ai-btn rounded-full px-5 py-2.5 font-semibold text-sm font-body inline-flex items-center gap-2">
                  {busy ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Verify DNS
                </button>
                <button data-testid="domain-remove-btn" onClick={removeDomain} disabled={busy} className="text-sm font-body text-[#52525b]">Remove</button>
              </div>
              <p className="font-body text-xs text-[#a1a1aa] mt-3">DNS changes can take a few minutes to a few hours. The domain goes live for visitors after you deploy the app.</p>
            </div>
          )}
        </div>
      )}

      {tab === "buy" && (
        <div className="text-center py-6">
          <Globe size={28} className="text-sky-500 mx-auto" />
          <div className="font-heading font-semibold text-[#09090b] mt-3">Buy a domain in-app</div>
          <p className="font-body text-sm text-[#52525b] mt-2">Search and purchase a new domain without leaving MapLift. This connects to a domain registrar — ask the MapLift team to enable it for your account.</p>
        </div>
      )}
    </div>
  );
}

function PublishSuccess({ siteUrl }) {
  const copy = () => { navigator.clipboard.writeText(siteUrl); toast.success("Link copied!"); };
  return (
    <div className="text-center">
      <div className="w-14 h-14 rounded-full bg-emerald-500 flex items-center justify-center mx-auto mb-4"><Check size={28} color="#fff" /></div>
      <h3 className="font-heading font-bold text-2xl text-[#09090b]">Website Published! 🎉</h3>
      <p className="font-body text-sm text-[#52525b] mt-2">Your professional website is now live.</p>
      <div className="flex items-center gap-2 bg-[#f4f4f5] rounded-xl px-3 py-2.5 mt-5">
        <span className="flex-1 font-mono-ml text-xs text-[#09090b] truncate text-left">{siteUrl}</span>
        <button data-testid="copy-link" onClick={copy} className="text-[#52525b]"><Copy size={16} /></button>
      </div>
      <div className="flex gap-3 mt-5">
        <a data-testid="visit-site" href={siteUrl} target="_blank" rel="noreferrer" className="flex-1 ml-ai-btn rounded-full py-3 font-semibold text-sm font-body inline-flex items-center justify-center gap-2">
          <ExternalLink size={15} /> Visit Website
        </a>
      </div>
      <img alt="QR code" className="mx-auto mt-6 rounded-lg border border-border" width={130} height={130}
        src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(siteUrl)}`} />
      <p className="font-body text-xs text-[#a1a1aa] mt-2">Scan to view · Download QR</p>
    </div>
  );
}

function ScorePill({ label, value = 0 }) {
  const color = value >= 90 ? "#10b981" : value >= 75 ? "#0ea5e9" : "#f59e0b";
  return (
    <div>
      <div className="flex justify-between items-center mb-1.5">
        <span className="font-body text-sm text-[#52525b] flex items-center gap-1.5"><Gauge size={14} /> {label}</span>
        <span className="font-mono-ml text-sm font-semibold" style={{ color }}>{value}/100</span>
      </div>
      <div className="h-2 bg-[#f4f4f5] rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${value}%`, background: color }} /></div>
    </div>
  );
}

function Modal({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-8 w-full max-w-md relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-[#a1a1aa]" data-testid="modal-close"><X size={18} /></button>
        {children}
      </div>
    </div>
  );
}

function slug(name) {
  return (name || "site").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40) || "site";
}

function buildSuggestions(project, website, update) {
  if (!project) return [];
  const intel = project.profile?.intelligence || {};
  const out = [];
  const sections = website.sections || [];
  const heroIdx = sections.findIndex((s) => s.type === "hero");
  const strength = (intel.strengths || [])[0];
  const theme = (intel.sentiment?.themes || [])[0];

  if (heroIdx >= 0 && strength) {
    out.push({
      title: "Lead with your #1 strength",
      desc: `Customers love: "${strength}". Add it to your hero subheadline.`,
      apply: () => {
        const w = JSON.parse(JSON.stringify(website));
        w.sections[heroIdx].subheadline = `${strength}. ${w.sections[heroIdx].subheadline || ""}`.trim();
        update(w);
        toast.success("Hero updated with your top strength.");
      },
    });
  }
  if (heroIdx >= 0 && theme) {
    out.push({
      title: "Add a review-based trust badge",
      desc: `Your reviews frequently mention "${theme}". Highlight it as a badge.`,
      apply: () => {
        const w = JSON.parse(JSON.stringify(website));
        const badges = w.sections[heroIdx].badges || [];
        if (!badges.includes(theme)) badges.unshift(theme);
        w.sections[heroIdx].badges = badges.slice(0, 4);
        update(w);
        toast.success("Trust badge added.");
      },
    });
  }
  out.push({
    title: "Punch up your primary color",
    desc: "Increase contrast on your accent color for stronger call-to-action buttons.",
    apply: () => { update({ ...website, colors: { ...website.colors, accent: website.colors.primary } }); toast.success("Accent aligned to brand primary."); },
  });
  return out;
}
